<template>
  <div class="min-h-screen bg-white">
    <!-- Header -->
    <div class="flex justify-between items-center w-full bg-white px-6 py-4 border-b">
      <div class="flex items-center space-x-6">
        <h1 class="text-2xl font-bold text-black">All Transactions</h1>
        <nav class="flex space-x-4">
          <router-link 
            to="/admin/dashboard" 
            class="text-black hover:text-purple-600"
            :class="{ 'text-purple-600 font-semibold': $route.path === '/admin/dashboard' }"
          >
            Dashboard
          </router-link>
          <router-link 
            to="/admin/transactions" 
            class="text-black hover:text-purple-600"
            :class="{ 'text-purple-600 font-semibold': $route.path === '/admin/transactions' }"
          >
            Transactions
          </router-link>
          <router-link 
            to="/admin/change-password" 
            class="text-black hover:text-purple-600"
            :class="{ 'text-purple-600 font-semibold': $route.path === '/admin/change-password' }"
          >
            Change Password
          </router-link>
        </nav>
      </div>
      <button @click="logout" class="btn btn-ghost text-black">Logout</button>
    </div>

    <div class="p-6">
      <!-- Search and Filters -->
      <div class="mb-6 flex flex-wrap gap-4">
        <div class="flex-1 min-w-[300px]">
          <input 
            type="text"
            v-model="searchQuery"
            placeholder="Search by ID, Wallet Address, or UPI ID..."
            class="input input-bordered w-full text-black"
            @input="handleSearch"
          />
        </div>
        <div class="flex gap-2">
          <select 
            v-model="statusFilter"
            class="select select-bordered text-black"
            @change="handleSearch"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
          <select 
            v-model="pageSize"
            class="select select-bordered text-black"
            @change="handleSearch"
          >
            <option :value="10">10 per page</option>
            <option :value="25">25 per page</option>
            <option :value="50">50 per page</option>
            <option :value="100">100 per page</option>
          </select>
        </div>
      </div>

      <!-- Data Grid -->
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="overflow-x-auto">
          <table class="table w-full">
            <thead>
              <tr>
                <th 
                  v-for="column in columns" 
                  :key="column.key"
                  class="text-black bg-gray-50 cursor-pointer hover:bg-gray-100"
                  @click="sortBy(column.key)"
                >
                  {{ column.label }}
                  <span v-if="sortColumn === column.key" class="ml-1">
                    {{ sortDirection === 'asc' ? '↑' : '↓' }}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody class="text-black">
              <tr v-for="tx in transactions" :key="tx.id" class="hover:bg-gray-50">
                <td class="font-mono text-sm">{{ tx.id }}</td>
                <td>₹{{ (tx.amount || 0).toFixed(2) }}</td>
                <td>{{ (tx.lrs_amount || 0).toFixed(4) }}</td>
                <td class="font-mono text-xs" :title="tx.wallet_address">
                  {{ tx.short_wallet }}
                </td>
                <td class="font-mono text-xs">{{ tx.upi_transaction_id || '-' }}</td>
                <td>
                  <span :class="{
                    'px-2 py-1 rounded text-sm': true,
                    'bg-yellow-100 text-yellow-800': tx.status === 'Pending',
                    'bg-green-100 text-green-800': tx.status === 'Completed',
                    'bg-red-100 text-red-800': tx.status === 'Rejected'
                  }" :title="tx.status === 'Rejected' ? `Reason: ${tx.rejection_reason}` : ''">
                    {{ tx.status }}
                  </span>
                </td>
                <td class="font-mono text-xs">
                  <a 
                    v-if="tx.tx_hash"
                    :href="`${explorerUrl}/tx/${tx.tx_hash}`"
                    target="_blank"
                    class="text-purple-600 hover:text-purple-800 hover:underline"
                    :title="tx.tx_hash"
                  >
                    {{ tx.tx_hash.substring(0, 6) + '...' + tx.tx_hash.slice(-4) }}
                  </a>
                  <span v-else>-</span>
                </td>
                <td class="text-sm">
                  {{ new Date(tx.created_at).toLocaleString() }}
                </td>
                <td>
                  <div class="flex gap-2">
                    <button 
                      v-if="tx.screenshot_path"
                      class="btn btn-xs btn-ghost"
                      @click="viewScreenshot(tx.screenshot_path)"
                      title="View Screenshot"
                    >
                      🖼️
                    </button>
                    <button 
                      v-if="tx.status === 'Pending'"
                      class="btn btn-xs btn-success"
                      @click="updateStatus(tx.id, 'completed')"
                      title="Approve"
                    >
                      ✓
                    </button>
                    <button 
                      v-if="tx.status === 'Pending'"
                      class="btn btn-xs btn-error"
                      @click="openRejectModal(tx.id)"
                      title="Reject"
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="flex justify-between items-center px-6 py-4 bg-gray-50">
          <div class="text-sm text-gray-700">
            Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ totalItems }} entries
          </div>
          <div class="flex gap-2">
            <button 
              class="btn btn-sm"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              Previous
            </button>
            <button 
              v-for="page in displayedPages" 
              :key="page"
              class="btn btn-sm"
              :class="{ 'btn-primary': currentPage === page }"
              @click="changePage(page)"
            >
              {{ page }}
            </button>
            <button 
              class="btn btn-sm"
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useEnv } from '../composables/useEnv'

// Add computed property for explorer URL
const explorerUrl = computed(() => import.meta.env.VITE_EXPLORER_URL)

// Column definitions
const columns = [
  { key: 'id', label: 'Transaction ID' },
  { key: 'amount', label: 'Amount (INR)' },
  { key: 'lrs_amount', label: 'LRS Amount' },
  { key: 'wallet_address', label: 'Wallet' },
  { key: 'upi_transaction_id', label: 'Payment ID' },
  { key: 'status', label: 'Status' },
  { key: 'tx_hash', label: 'Blockchain TX' },
  { key: 'created_at', label: 'Date' },
  { key: 'actions', label: 'Actions' }
]

// State
const transactions = ref([])
const totalItems = ref(0)
const currentPage = ref(1)
const pageSize = ref(10)
const searchQuery = ref('')
const statusFilter = ref('')
const sortColumn = ref('created_at')
const sortDirection = ref('desc')

// Computed
const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value))
const startIndex = computed(() => (currentPage.value - 1) * pageSize.value)
const endIndex = computed(() => Math.min(startIndex.value + pageSize.value, totalItems.value))

const displayedPages = computed(() => {
  const delta = 2
  const range = []
  for (
    let i = Math.max(1, currentPage.value - delta);
    i <= Math.min(totalPages.value, currentPage.value + delta);
    i++
  ) {
    range.push(i)
  }
  return range
})

// Methods
const { apiUrl } = useEnv()

const fetchTransactions = async () => {
  try {
    const token = localStorage.getItem('adminToken')
    const params = new URLSearchParams({
      page: currentPage.value.toString(),
      limit: pageSize.value.toString(),
      search: searchQuery.value,
      status: statusFilter.value,
      sortBy: sortColumn.value,
      sortDir: sortDirection.value
    })

    const response = await fetch(`${apiUrl}/api/admin/transactions?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) throw new Error('Failed to fetch transactions')

    const data = await response.json()
    transactions.value = data.transactions
    totalItems.value = data.total
  } catch (error) {
    console.error('Error fetching transactions:', error)
    alert('Failed to fetch transactions')
  }
}

const handleSearch = () => {
  currentPage.value = 1
  fetchTransactions()
}

const changePage = (page: number) => {
  currentPage.value = page
  fetchTransactions()
}

const sortBy = (column: string) => {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortColumn.value = column
    sortDirection.value = 'asc'
  }
  fetchTransactions()
}

const router = useRouter()

const logout = () => {
  localStorage.removeItem('adminToken')
  router.push('/admin/login')
}

onMounted(() => {
  fetchTransactions()
})
</script>

<style scoped>
/* Add your existing styles here */
</style> 